import { buildApiUrl, buildAssetUrl } from '../../utils/api';
import { getCookie } from '../../utils/cookies';
import { apiFetch } from '../../utils/apiFetch';
import toast from 'react-hot-toast';

const CART_BASE = buildApiUrl('api/v1/customer/cart');
const GUEST_CART_KEY = 'kkisan_guest_cart';

// ─── Helpers ────────────────────────────────────────────────────────────────

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getCookie('authToken')}`,
});

const isLoggedIn = () => !!getCookie('authToken');

// Guest cart persistence
const getGuestCart = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]'); }
  catch { return []; }
};
const saveGuestCart = (cart) => localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
const clearGuestCart = () => localStorage.removeItem(GUEST_CART_KEY);

// Extract product_id from object or raw id
const getProductId = (item) =>
  typeof item === 'object' ? (item.product_id ?? item.id) : item;

// Extract selected variant from product object
const getSelectedVariant = (item = {}) =>
  (typeof item.selectedVariant === 'object' && item.selectedVariant)
    ? item.selectedVariant
    : null;

// Extract variant ids from item/product
const getVariantIds = (item = {}) => {
  const sv = getSelectedVariant(item) || {};
  const pvid =
    item.product_variant_id ??
    sv.product_variant_id ??
    item.variant_id ??
    sv.variant_id ??
    sv.id ??
    null;
  const vid =
    item.variant_id ??
    sv.variant_id ??
    item.product_variant_id ??
    sv.product_variant_id ??
    sv.id ??
    null;
  return {
    product_variant_id: pvid != null ? Number(pvid) : null,
    variant_id: vid != null ? Number(vid) : null,
  };
};

// Check if two cart items refer to the same product+variant
const sameCartItem = (item, product_id, product_variant_id) => {
  if (String(item.product_id) !== String(product_id)) return false;
  if (product_variant_id) {
    return String(item.product_variant_id) === String(product_variant_id);
  }
  return !item.product_variant_id;
};

// Payload for POST /cart/add  →  { product_id, quantity, product_variant_id? }
const buildAddPayload = (product_id, quantity, product_variant_id) => {
  const p = { product_id: Number(product_id), quantity: Number(quantity) };
  if (product_variant_id) p.product_variant_id = Number(product_variant_id);
  return p;
};

// Payload for PATCH increment/decrement & DELETE /cart/item  →  { product_id, product_variant_id? }
const buildUpdatePayload = (product_id, product_variant_id) => {
  const p = { product_id: Number(product_id) };
  if (product_variant_id) p.product_variant_id = Number(product_variant_id);
  return p;
};

const getVariantLabel = (variant) => {
  if (!variant) return null;
  if (typeof variant === 'string') return variant.trim() || null;
  const vn = variant.variant_name ?? variant.size ?? variant.name ?? '';
  const un = variant.unit_name ?? variant.unit ?? '';
  return [vn, un]
    .map((s) => String(s).trim())
    .filter(Boolean)
    .filter((v, i, a) => v !== 'unit' && a.indexOf(v) === i)
    .join(' ') || null;
};

// Normalize a raw cart item from the API into a consistent shape
const normalizeItem = (item, fallback = null) => {
  // p is ONLY the nested product sub-object (if present), never the item itself
  const p = (item.product && typeof item.product === 'object') ? item.product : {};
  const fb = fallback ?? {};

  const product_variant_id = item.product_variant_id ?? p.product_variant_id ?? null;
  const variant_id         = item.variant_id ?? p.variant_id ?? null;

  const rawVariantName = item.variant_name ?? p.variant_name ?? '';
  const rawUnitName = item.unit_name ?? item.unit ?? item.quantity_unit ?? p.unit_name ?? p.unit ?? p.quantity_unit ?? '';
  let variantLabel = [rawVariantName, rawUnitName]
    .map((s) => String(s).trim())
    .filter(Boolean)
    .filter((v, i, a) => v !== 'unit' && a.indexOf(v) === i)
    .join(' ') || getVariantLabel(item.variant ?? p.variant ?? null);

  // Fallback to catalog product variant/unit when cart item lacks variant information
  if (!variantLabel && fb) {
    const pvid = product_variant_id ?? variant_id;
    const matchedVariant = (Array.isArray(fb.variants) && fb.variants.length > 0)
      ? (pvid ? fb.variants.find((v) => String(v.product_variant_id ?? v.variant_id ?? v.id) === String(pvid)) : null) || fb.variants[0]
      : null;

    if (matchedVariant) {
      variantLabel = getVariantLabel(matchedVariant);
    }
    if (!variantLabel && fb.unit && fb.unit !== 'unit') {
      variantLabel = String(fb.unit).trim();
    } else if (!variantLabel && fb.unit_name) {
      variantLabel = String(fb.unit_name).trim();
    }
  }

  return {
    cart_item_id:       item.cart_item_id ?? item.id ?? null,
    product_id:         item.product_id ?? p.product_id ?? p.id ?? fb.product_id ?? fb.id,
    product_variant_id,
    variant_id,
    // item-level name fields take strict priority — prevents stale product store data leaking in
    name:  item.product_name ?? item.name ?? p.name ?? p.product_name ?? fb.name ?? 'Product',
    image: buildAssetUrl(
      item.image ?? item.image1 ?? item.product_image ??
      p.image ?? p.image1 ?? p.product_image ??
      fb.image ?? fb.image1 ?? null
    ),
    price: parseFloat(
      item.discount_price ?? item.variant_price ?? item.price ??
      p.discount_price ?? p.variant_price ?? p.price ??
      fb.price ?? fb.starting_price ?? 0
    ),
    originalPrice: parseFloat(
      item.original_price ?? item.originalPrice ??
      p.original_price ?? p.originalPrice ??
      fb.original_price ?? fb.originalPrice ?? 0
    ),
    gst: parseFloat(
      item.gst ?? item.gst_percentage ??
      p.gst ?? p.gst_percentage ??
      fb.gst ?? fb.gst_percentage ?? 0
    ),
    item_total: parseFloat(item.item_total ?? item.total_price ?? 0),
    quantity:   parseInt(item.quantity ?? item.qty ?? 1, 10),
    variant:    variantLabel,
    // Preserve availability details so checkout can identify every affected item.
    in_stock: item.in_stock ?? item.inStock ?? p.in_stock ?? p.inStock ?? fb.in_stock ?? fb.inStock,
    // Location-aware stock fields from the cart API
    available_stock: item.available_stock ?? p.available_stock ?? fb.available_stock ?? null,
    stock_status: item.stock_status ?? p.stock_status ?? fb.stock_status ?? null,
    stock_message: item.stock_message ?? p.stock_message ?? fb.stock_message ?? null,
    is_out_of_stock: item.is_out_of_stock ?? (item.stock_status === 'OUT_OF_STOCK') ?? false,
    stock: item.stock ?? item.available_stock ?? p.stock ?? p.available_stock ?? fb.stock ?? fb.available_stock,
    delivery_available:
      item.delivery_available ?? item.is_delivery_available ??
      p.delivery_available ?? p.is_delivery_available ??
      fb.delivery_available ?? fb.is_delivery_available,
  };
};

// The cart badge represents the total units in the cart, not just the number
// of different product lines.
const calcCounts = (cart) => ({
  cart_count: cart.reduce(
    (total, item) => total + Math.max(0, parseInt(item.quantity ?? item.qty ?? 0, 10) || 0),
    0,
  ),
});

// Compute GST from local cart items (used when API can't return tax for guests)
// Each item stores its gst field as a percentage (e.g. 5 = 5%).
// Tax = price × qty × (gst% / 100), summed across all items.
const calcGstFromItems = (cart) =>
  cart.reduce((total, i) => {
    const price = parseFloat(i.price || 0);
    const qty   = parseInt(i.quantity || 1, 10);
    const gstPct = parseFloat(i.gst || i.gst_percentage || 0);
    return total + (price * qty * gstPct) / 100;
  }, 0);

// Optimistically recalculate cart_meta from local cart items
const calcCartMeta = (cart, prevMeta = {}) => {
  const subtotal   = cart.reduce((s, i) => s + parseFloat(i.price || 0) * parseInt(i.quantity || 1, 10), 0);
  const tax_amount = calcGstFromItems(cart);
  return {
    ...prevMeta,
    subtotal,
    tax_amount,
    delivery_fee: 0,
    total_amount: subtotal + tax_amount,
    free_delivery_min_amount: 0,
    amount_needed_for_free_delivery: 0,
  };
};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const createCartSlice = (set, get) => ({
  cart: [],
  cart_count: 0,
  cart_total: 0,
  cart_gst_total: 0,
  // Server-provided totals (populated after fetchCart)
  cart_meta: {
    subtotal: 0,
    tax_amount: 0,
    delivery_fee: 0,
    delivery_options: [],
    discount_amount: 0,
    applied_coupon: null,
    coupon_detached_reason: null,
    total_amount: 0,
    total_items: 0,
    total_quantity: 0,
    free_delivery_min_amount: 0,
    amount_needed_for_free_delivery: 0,
    applied_pincode: null,
    round_off: 0,
    rounded_total_amount: 0,
    final_payable: 0,
    applied_location: null,
    serviceable: null,
    matched_store_id: null,
  },
  cart_loading: false,
  cart_error: null,
  is_guest_cart: false,
  available_coupons: [],
  coupons_loading: false,
  coupon_action_loading: false,

  // ── Helper to update store from API response data ──
  processCartResponse: (raw) => {
    if (!raw) return;
    const knownProducts = get().products || [];
    const productDetail = get().productDetail;

    const findFallback = (item) => {
      const pid = item.product_id ?? item.product?.product_id ?? item.id;
      if (productDetail && String(productDetail.product_id ?? productDetail.id) === String(pid))
        return productDetail;
      return knownProducts.find((p) =>
        String(p.id) === String(pid) || String(p.product_id) === String(pid)
      );
    };

    const items = (
      raw?.items ?? raw?.cart_items ?? raw?.cartItems ??
      (Array.isArray(raw) ? raw : [])
    ).map((item) => normalizeItem(item, findFallback(item)));

    const cart_meta = {
      subtotal:                        parseFloat(raw?.subtotal ?? 0),
      tax_amount:                      parseFloat(raw?.tax_amount ?? 0),
      delivery_fee:                    parseFloat(raw?.delivery_fee ?? 0),
      delivery_options:                Array.isArray(raw?.delivery_options) ? raw.delivery_options : [],
      discount_amount:                 parseFloat(raw?.discount_amount ?? raw?.applied_coupon?.discount_amount ?? 0),
      applied_coupon:                  raw?.applied_coupon ?? null,
      coupon_detached_reason:          raw?.coupon_detached_reason ?? null,
      total_amount:                    parseFloat(raw?.total_amount ?? raw?.subtotal ?? 0),
      total_items:                     parseInt(raw?.total_items ?? 0, 10),
      total_quantity:                  parseInt(raw?.total_quantity ?? 0, 10),
      free_delivery_min_amount:        parseFloat(raw?.free_delivery_min_amount ?? 0),
      amount_needed_for_free_delivery: parseFloat(raw?.amount_needed_for_free_delivery ?? 0),
      applied_pincode:                 raw?.applied_pincode ?? null,
      // Rounded totals & round-off
      round_off:                       parseFloat(raw?.round_off ?? 0),
      rounded_total_amount:            parseFloat(raw?.rounded_total_amount ?? raw?.total_amount ?? 0),
      final_payable:                   parseFloat(raw?.final_payable ?? raw?.rounded_total_amount ?? raw?.total_amount ?? 0),
      // Location info
      applied_location:                raw?.applied_location ?? null,
      serviceable:                     raw?.serviceable ?? raw?.is_pincode_serviceable ?? null,
      matched_store_id:                raw?.matched_store_id ?? null,
      // API-confirmed values — use these directly, no local recalculation needed
      selected_delivery_type:          raw?.selected_delivery_type ?? null,
      is_pincode_serviceable:          raw?.is_pincode_serviceable ?? null,
      has_out_of_stock_items:          raw?.has_out_of_stock_items ?? false,
      can_proceed_to_checkout:         raw?.can_proceed_to_checkout ?? true,
    };

    if (cart_meta.coupon_detached_reason) {
      toast.error(`Your coupon was removed: ${cart_meta.coupon_detached_reason}`, { id: 'coupon-detached' });
    }

    set({
      cart: items,
      ...calcCounts(items),
      cart_total: cart_meta.total_amount,
      cart_gst_total: cart_meta.tax_amount,
      cart_meta,
      cart_loading: false,
      is_guest_cart: false,
    });
  },

  // ── Load guest cart from localStorage (unauthenticated) ──
  loadGuestCart: () => {
    if (isLoggedIn()) return;
    const items       = getGuestCart();
    const subtotal    = items.reduce((sum, i) => sum + parseFloat(i.price || 0) * parseInt(i.quantity || 1, 10), 0);
    const tax_amount  = calcGstFromItems(items);   // real GST computed from item gst% fields
    const totalQuantity = items.reduce((sum, i) => sum + parseInt(i.quantity || 1, 10), 0);
    const cart_meta = {
      ...get().cart_meta,
      subtotal,
      tax_amount,
      delivery_fee: 0,
      delivery_options: [],
      total_amount: subtotal + tax_amount,
      total_items: items.length,
      total_quantity: totalQuantity,
      free_delivery_min_amount: 0,
      amount_needed_for_free_delivery: 0,
      applied_pincode: null,
      discount_amount: 0,
      applied_coupon: null,
      coupon_detached_reason: null,
    };
    set({
      cart: items,
      ...calcCounts(items),
      cart_meta,
      cart_total: subtotal + tax_amount,
      cart_gst_total: tax_amount,
      is_guest_cart: true,
    });
  },

  // ── Merge guest cart items to server after login ──
  // Guard flag: prevents double-invocation from React StrictMode or multiple auth callbacks
  _mergingGuestCart: false,

  mergeGuestCartOnLogin: async () => {
    // Prevent concurrent / double calls
    if (get()._mergingGuestCart) return;

    const guestItems = getGuestCart();
    if (!guestItems.length) {
      set({ is_guest_cart: false });
      return;
    }

    set({ _mergingGuestCart: true });

    // Clear localStorage IMMEDIATELY so a re-render or second call won't find items again
    clearGuestCart();
    set({ is_guest_cart: false });

    let failedCount = 0;
    for (const item of guestItems) {
      try {
        const res = await fetch(`${CART_BASE}/add`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(buildAddPayload(
            item.product_id,
            item.quantity,
            item.product_variant_id
          )),
        });
        const json = await res.json().catch(() => ({}));
        // 409 Conflict means the item already exists on the server — treat as success
        if (!res.ok && res.status !== 409) {
          if (!json.success) failedCount++;
        }
      } catch {
        failedCount++;
      }
    }

    set({ _mergingGuestCart: false });
    await get().fetchCart();

    if (failedCount > 0) {
      toast.error(
        `${failedCount} item(s) could not be synced. Please re-add them.`,
        { id: 'guest-cart-sync-error' }
      );
    } else {
      toast.success(
        `${guestItems.length} item(s) from your cart have been saved!`,
        { id: 'guest-cart-sync-success' }
      );
    }
  },

  // ── GET /api/v1/customer/cart ──
  fetchCart: async (pincode = null, deliveryType = null, addressId = null) => {
    set({ cart_loading: true, cart_error: null });
    try {
      const params = new URLSearchParams();
      if (pincode) params.set('pincode', String(pincode).trim());
      if (deliveryType) params.set('delivery_type', String(deliveryType).toUpperCase());
      if (addressId) params.set('address_id', String(addressId));
      const url = params.toString() ? `${CART_BASE}?${params.toString()}` : CART_BASE;

      // Call the API with auth header if logged in, without it for guest users
      const headers = isLoggedIn() ? authHeaders() : { 'Content-Type': 'application/json' };
      const res = await fetch(url, { headers });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to fetch cart');

      if (isLoggedIn()) {
        // Authenticated: use API response as source of truth
        get().processCartResponse(json.data);
      } else {
        // Guest: API returns empty items[] (server has no guest cart).
        // Use localStorage items as source of truth; compute GST locally from gst% per item.
        // Only use API delivery_options/delivery_fee if the server provides them.
        const guestItems    = getGuestCart();
        const subtotal      = guestItems.reduce((sum, i) => sum + parseFloat(i.price || 0) * parseInt(i.quantity || 1, 10), 0);
        const tax_amount    = calcGstFromItems(guestItems);  // real GST from item gst% fields
        const totalQuantity = guestItems.reduce((sum, i) => sum + parseInt(i.quantity || 1, 10), 0);
        const apiData       = json.data || {};
        // API delivery_fee is meaningful only when the server knows the items — for guests it's 0.
        const delivery_fee  = parseFloat(apiData.delivery_fee ?? 0);
        const cart_meta = {
          subtotal,
          tax_amount,
          delivery_fee,
          delivery_options:                Array.isArray(apiData.delivery_options) ? apiData.delivery_options : [],
          total_amount:                    subtotal + tax_amount + delivery_fee,
          total_items:                     guestItems.length,
          total_quantity:                  totalQuantity,
          free_delivery_min_amount:        parseFloat(apiData.free_delivery_min_amount ?? 0),
          amount_needed_for_free_delivery: parseFloat(apiData.amount_needed_for_free_delivery ?? 0),
          applied_pincode:                 apiData.applied_pincode ?? null,
          discount_amount:                 0,
          applied_coupon:                  null,
          coupon_detached_reason:          null,
        };
        set({
          cart: guestItems,
          ...calcCounts(guestItems),
          cart_meta,
          cart_total: cart_meta.total_amount,
          cart_gst_total: tax_amount,
          cart_loading: false,
          is_guest_cart: true,
        });
      }
    } catch (err) {
      // If API fails for guest, fall back to local guest cart data
      if (!isLoggedIn()) {
        get().loadGuestCart();
      } else {
        set({ cart_error: err.message, cart_loading: false });
      }
    }
  },

  fetchAvailableCoupons: async ({ placement = 'checkout', subtotal = 0, orderType = 'STANDARD' } = {}) => {
    set({ coupons_loading: true });
    try {
      const params = new URLSearchParams({ placement, subtotal: String(subtotal), order_type: String(orderType) });
      const json = await apiFetch(`api/v1/customer/coupons?${params.toString()}`);
      set({ available_coupons: Array.isArray(json.data) ? json.data : [], coupons_loading: false });
      return json.data || [];
    } catch {
      set({ coupons_loading: false });
      return [];
    }
  },

  applyCartCoupon: async (couponCode, deliveryType, addressId) => {
    const code = String(couponCode || '').trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code first.'); return false; }
    if (!isLoggedIn()) { toast.error('Please sign in to apply a coupon.'); return false; }
    set({ coupon_action_loading: true });
    try {
      const json = await apiFetch('api/v1/customer/cart/apply-coupon', {
        method: 'POST',
        body: {
          coupon_code: code,
          delivery_type: String(deliveryType || 'STANDARD').toUpperCase() === 'EXPRESS'
            ? 'express_delivery'
            : (String(deliveryType || 'STANDARD').toUpperCase() === 'SCHEDULED' ? 'normal_delivery' : 'STANDARD'),
          ...(addressId ? { address_id: Number(addressId) } : {}),
        },
      });
      if (json.data?.cart) get().processCartResponse(json.data.cart);
      else if (json.data?.cart_summary) {
        const current = get().cart_meta;
        const summary = json.data.cart_summary;
        const coupon = json.data.coupon ?? json.data;
        const cart_meta = {
          ...current,
          subtotal: Number(summary.subtotal ?? current.subtotal),
          discount_amount: Number(summary.discount_amount ?? json.data.discount_amount ?? 0),
          tax_amount: Number(summary.tax_amount ?? current.tax_amount),
          delivery_fee: Number(summary.delivery_fee ?? current.delivery_fee),
          total_amount: Number(summary.total_amount ?? current.total_amount),
          applied_coupon: { ...coupon, discount_amount: Number(json.data.discount_amount ?? summary.discount_amount ?? coupon.discount_amount ?? 0) },
          coupon_detached_reason: null,
        };
        set({ cart_meta, cart_total: cart_meta.total_amount, cart_gst_total: cart_meta.tax_amount });
      }
      await get().fetchCart(null, deliveryType, addressId);
      toast.success(json.message || `Coupon ${code} applied.`);
      set({ coupon_action_loading: false });
      return true;
    } catch (err) {
      set({ coupon_action_loading: false });
      const couponMessages = {
        INVALID_CODE: 'Invalid coupon code. Please check and retry.',
        COUPON_INACTIVE: 'This promo code is currently inactive.',
        EXPIRED: 'This coupon code has expired.',
        NO_ELIGIBLE_ITEMS: 'None of the items in your cart are eligible for this coupon.',
        FIRST_ORDER_ONLY: 'This offer is exclusive to your first order.',
        NOT_ELIGIBLE: 'This coupon is not valid for your account.',
        CUST_LIMIT_REACHED: 'You have already redeemed this coupon the maximum allowed times.',
        GLOBAL_LIMIT_REACHED: 'This offer has reached its maximum redemptions.',
      };
      const message = err.code === 'MIN_ORDER_NOT_MET' && err.amount_needed != null
        ? `Add ₹${Number(err.amount_needed).toFixed(2)} more to activate ${code}.`
        : couponMessages[err.code] || err.message || 'Could not apply this coupon.';
      toast.error(message);
      return false;
    }
  },

  removeCartCoupon: async (deliveryType, addressId) => {
    set({ coupon_action_loading: true });
    try {
      const json = await apiFetch('api/v1/customer/cart/remove-coupon', { method: 'POST', body: {} });
      if (json.data?.cart) get().processCartResponse(json.data.cart);
      else if (json.data?.cart_summary) {
        const current = get().cart_meta;
        const summary = json.data.cart_summary;
        const cart_meta = {
          ...current,
          subtotal: Number(summary.subtotal ?? current.subtotal),
          discount_amount: Number(summary.discount_amount ?? 0),
          tax_amount: Number(summary.tax_amount ?? current.tax_amount),
          delivery_fee: Number(summary.delivery_fee ?? current.delivery_fee),
          total_amount: Number(summary.total_amount ?? current.total_amount),
          applied_coupon: null,
          coupon_detached_reason: null,
        };
        set({ cart_meta, cart_total: cart_meta.total_amount, cart_gst_total: cart_meta.tax_amount });
      }
      await get().fetchCart(null, deliveryType, addressId);
      toast.success(json.message || 'Coupon removed.');
      set({ coupon_action_loading: false });
      return true;
    } catch (err) {
      set({ coupon_action_loading: false });
      toast.error(err.message || 'Could not remove the coupon.');
      return false;
    }
  },

  // ── POST /api/v1/customer/cart/add ──
  // Body: { product_id, quantity, product_variant_id? }
  addToCart: async (productOrId, quantity = 1) => {
    const product_id = getProductId(productOrId);
    if (!product_id) return;

    const source = typeof productOrId === 'object' ? productOrId : {};
    let selectedVariant = getSelectedVariant(source);
    let { product_variant_id } = getVariantIds(source);

    // If variant is missing (e.g. added from Related Products), resolve default variant from store catalog
    const knownProducts = get().products || [];
    const catalogProduct = knownProducts.find((p) =>
      String(p.id) === String(product_id) || String(p.product_id) === String(product_id)
    );
    if (!selectedVariant && catalogProduct?.variants?.length) {
      selectedVariant = catalogProduct.variants[0];
    }
    if (!product_variant_id && selectedVariant) {
      product_variant_id = selectedVariant.product_variant_id ?? selectedVariant.variant_id ?? null;
    }

    // ── Guest (not logged in) ──
    if (!isLoggedIn()) {
      const guestCart = getGuestCart();
      const existing = guestCart.find((i) => sameCartItem(i, product_id, product_variant_id));
      const fromStore = catalogProduct || knownProducts.find((p) =>
        String(p.id) === String(product_id) || String(p.product_id) === String(product_id)
      );
      const product = Object.keys(source).length ? source : fromStore;

      const updated = existing
        ? guestCart.map((i) =>
            sameCartItem(i, product_id, product_variant_id)
              ? { ...i, quantity: i.quantity + quantity }
              : i
          )
        : [...guestCart, {
            product_id,
            product_variant_id: product_variant_id ?? null,
            variant_id: selectedVariant?.variant_id ?? null,
            name:  product?.name ?? product?.product_name ?? fromStore?.name ?? 'Product',
            image: product?.image ?? product?.image1 ?? fromStore?.image ?? null,
            price: parseFloat(selectedVariant?.discount_price ?? product?.price ?? fromStore?.price ?? 0),
            originalPrice: parseFloat(selectedVariant?.original_price ?? product?.originalPrice ?? fromStore?.originalPrice ?? 0),
            gst: parseFloat(product?.gst ?? product?.gst_percentage ?? fromStore?.gst ?? 0),
            quantity,
            variant: getVariantLabel(selectedVariant) || (product?.unit && product.unit !== 'unit' ? product.unit : null) || (fromStore?.unit && fromStore.unit !== 'unit' ? fromStore.unit : null),
            cart_item_id: null,
          }];

      saveGuestCart(updated);
      set({ cart: updated, ...calcCounts(updated), is_guest_cart: true });
      return;
    }

    // ── Authenticated ──
    // Optimistically update the cart array immediately so all ProductCard components
    // across every page show the correct quantity stepper without waiting for the server.
    const currentCart = get().cart;
    const existingItem = currentCart.find((i) => sameCartItem(i, product_id, product_variant_id));
    const optimisticCart = existingItem
      ? currentCart.map((i) =>
          sameCartItem(i, product_id, product_variant_id)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        )
      : [
          ...currentCart,
          {
            product_id: Number(product_id),
            product_variant_id: product_variant_id ? Number(product_variant_id) : null,
            variant_id: selectedVariant?.variant_id ? Number(selectedVariant.variant_id) : null,
            name: source?.name ?? source?.product_name ?? catalogProduct?.name ?? 'Product',
            image: source?.image ?? source?.image1 ?? catalogProduct?.image ?? null,
            price: parseFloat(selectedVariant?.discount_price ?? source?.price ?? catalogProduct?.price ?? 0),
            originalPrice: parseFloat(selectedVariant?.original_price ?? source?.originalPrice ?? catalogProduct?.originalPrice ?? 0),
            gst: parseFloat(source?.gst ?? source?.gst_percentage ?? catalogProduct?.gst ?? 0),
            quantity,
            variant: getVariantLabel(selectedVariant) || (catalogProduct?.unit && catalogProduct.unit !== 'unit' ? catalogProduct.unit : null),
            cart_item_id: null,
          },
        ];
    set({ cart: optimisticCart, ...calcCounts(optimisticCart), cart_loading: true, cart_error: null });

    try {
      const res = await fetch(`${CART_BASE}/add`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(buildAddPayload(product_id, quantity, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to add to cart');

      if (json.data) {
        get().processCartResponse(json.data);
      } else {
        await get().fetchCart();
      }

      // A product moved to the cart should no longer remain in the wishlist.
      if (get().isInWishlist?.(product_id)) {
        const wishlistItem = get().wishlist?.find(
          (item) => String(item.product_id) === String(product_id),
        );
        await get().removeFromWishlist?.(
          product_id,
          wishlistItem?.variant_id ?? selectedVariant?.variant_id ?? null,
        );
      }
    } catch (err) {
      // Rollback to pre-optimistic cart on failure
      set({ cart: currentCart, ...calcCounts(currentCart), cart_error: err.message, cart_loading: false });
      toast.error(err.message || 'Failed to add to cart', {
        id: `cart-add-error-${product_id}`,
      });
    }
  },

  // ── PATCH /api/v1/customer/cart/item/increment ──
  // Body: { product_id, product_variant_id? }
  incrementItem: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const updated = get().cart.map((i) =>
      sameCartItem(i, product_id, product_variant_id)
        ? { ...i, quantity: i.quantity + 1 }
        : i
    );

    if (!isLoggedIn() || get().is_guest_cart) {
      const cart_meta = calcCartMeta(updated, get().cart_meta);
      saveGuestCart(updated);
      set({ cart: updated, ...calcCounts(updated), cart_meta, cart_total: cart_meta.total_amount, cart_gst_total: cart_meta.tax_amount, cart_loading: false, is_guest_cart: true });
      return;
    }

    // Optimistically update items and badge count without overwriting server cart_meta
    set({ cart: updated, ...calcCounts(updated), cart_error: null });

    try {
      const res = await fetch(`${CART_BASE}/item/increment`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to increment');

      if (json.data) {
        get().processCartResponse(json.data);
      } else {
        await get().fetchCart();
      }
    } catch (err) {
      set({ cart_error: err.message, cart_loading: false });
      toast.error(err.message || 'Failed to update quantity', {
        id: `cart-qty-error-${product_id}`,
      });
      await get().fetchCart();
    }
  },

  // ── PATCH /api/v1/customer/cart/item/decrement ──
  // Body: { product_id, product_variant_id? }
  decrementItem: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const cartItem = get().cart.find((i) => sameCartItem(i, product_id, product_variant_id));
    if (!cartItem) return;
    if (cartItem.quantity <= 1) { await get().removeFromCart(productOrItem); return; }

    const updated = get().cart.map((i) =>
      sameCartItem(i, product_id, product_variant_id)
        ? { ...i, quantity: i.quantity - 1 }
        : i
    );

    if (!isLoggedIn() || get().is_guest_cart) {
      const cart_meta = calcCartMeta(updated, get().cart_meta);
      saveGuestCart(updated);
      set({ cart: updated, ...calcCounts(updated), cart_meta, cart_total: cart_meta.total_amount, cart_gst_total: cart_meta.tax_amount, cart_loading: false, is_guest_cart: true });
      return;
    }

    set({ cart: updated, ...calcCounts(updated), cart_error: null });

    try {
      const res = await fetch(`${CART_BASE}/item/decrement`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to decrement');

      if (json.data) {
        get().processCartResponse(json.data);
      } else {
        await get().fetchCart();
      }
    } catch (err) {
      set({ cart_error: err.message, cart_loading: false });
      toast.error(err.message || 'Failed to update quantity', {
        id: `cart-qty-error-${product_id}`,
      });
      await get().fetchCart();
    }
  },

  // ── DELETE /api/v1/customer/cart/item ──
  // Body: { product_id, product_variant_id? }
  removeFromCart: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const updated = get().cart.filter((i) => !sameCartItem(i, product_id, product_variant_id));

    if (!isLoggedIn() || get().is_guest_cart) {
      const cart_meta = calcCartMeta(updated, get().cart_meta);
      saveGuestCart(updated);
      set({ cart: updated, ...calcCounts(updated), cart_meta, cart_total: cart_meta.total_amount, cart_gst_total: cart_meta.tax_amount, cart_loading: false, is_guest_cart: true });
      return;
    }

    set({ cart: updated, ...calcCounts(updated), cart_loading: true, cart_error: null });

    try {
      const res = await fetch(`${CART_BASE}/item`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to remove item');

      if (json.data) {
        get().processCartResponse(json.data);
      } else {
        await get().fetchCart();
      }
    } catch (err) {
      set({ cart_error: err.message, cart_loading: false });
      toast.error(err.message || 'Failed to remove item', {
        id: `cart-remove-error-${product_id}`,
      });
      await get().fetchCart();
    }
  },

  // ── DELETE /api/v1/customer/cart/clear ──
  clearCart: async () => {
    set({ cart: [], cart_count: 0, cart_total: 0, cart_gst_total: 0, cart_meta: { subtotal: 0, tax_amount: 0, delivery_fee: 0, delivery_options: [], total_amount: 0, total_items: 0, total_quantity: 0, free_delivery_min_amount: 0, amount_needed_for_free_delivery: 0, applied_pincode: null } });
    if (!isLoggedIn() || get().is_guest_cart) {
      clearGuestCart();
      set({ cart_loading: false, is_guest_cart: true });
      return;
    }

    try {
      const res = await fetch(`${CART_BASE}/clear`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to clear cart');
    } catch (err) {
      set({ cart_error: err.message, cart_loading: false });
      toast.error(err.message || 'Failed to clear cart', {
        id: 'cart-clear-error',
      });
      await get().fetchCart();
    }
  },

  getCartTotal: () => get().cart_total,
  getCartCount: () => get().cart_count,

  // ── Restore cart items after failed/cancelled payment ──
  restoreCartFromSnapshot: async (snapshotItems = []) => {
    if (!snapshotItems || !snapshotItems.length) return;

    // ── Optimistic restore: put items back in local state immediately ──
    // This ensures the UI never shows an empty cart while API calls are in-flight.
    const optimisticCart = snapshotItems.map((it) => ({
      product_id: it.product_id ?? it.id,
      id:         it.product_id ?? it.id,
      quantity:   it.quantity ?? 1,
      product_variant_id: it.product_variant_id ?? null,
      variant_id:         it.product_variant_id ?? null,
      // Preserve any extra fields (name, price, image) from original cart item
      ...it,
    }));
    set({
      cart: optimisticCart,
      cart_count: optimisticCart.length,
      cart_loading: true,
    });

    try {
      if (isLoggedIn()) {
        // Clear server cart then re-add each item
        await fetch(`${CART_BASE}/clear`, { method: 'DELETE', headers: authHeaders() });
        for (const item of snapshotItems) {
          const pid = Number(item.product_id ?? item.id);
          if (!pid) continue;
          const payload = buildAddPayload(
            pid,
            Number(item.quantity ?? 1),
            item.product_variant_id ? Number(item.product_variant_id) : undefined
          );
          await fetch(`${CART_BASE}/add`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(payload),
          });
        }
        // Sync final cart state from server
        await get().fetchCart();
      }
    } catch (err) {
      console.error('Failed to restore cart from snapshot:', err);
      set({ cart_loading: false });
      // Keep the optimistic local state — user can still see and interact with their items
      toast.error('Could not sync cart with server. Your items are shown but please refresh if totals look wrong.', {
        id: 'cart-restore-error',
        duration: 6000,
      });
    }
  },

  // ── POST /api/v1/customer/products/check-stock ──
  // (also aliased at POST /api/v1/customer/cart/check-stock)
  // Validates an array of products/variants against the matched store inventory.
  // Location resolution follows the priority: address_id > latitude/longitude > pincode
  checkCartStock: async ({ pincode, address_id, latitude, longitude, cart_items }) => {
    // The API expects `items` (not `cart_items`) per the documented schema
    const payload = {
      items: (cart_items || []).map((it) => ({
        product_id: Number(it.product_id ?? it.id),
        product_variant_id: it.product_variant_id ? Number(it.product_variant_id) : (it.variant_id ? Number(it.variant_id) : null),
        quantity: Number(it.quantity ?? it.qty ?? 1),
      })),
    };

    // Attach location param following resolution priority
    if (address_id) {
      payload.address_id = Number(address_id);
    } else if (latitude && longitude) {
      payload.latitude = Number(latitude);
      payload.longitude = Number(longitude);
    } else if (pincode) {
      payload.pincode = String(pincode).trim();
    }

    // Try the canonical endpoint first; fall back to the cart alias
    const endpoint = `${CART_BASE}/check-stock`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    if (!res.ok || json.success === false) {
      throw new Error(json.message || `Failed to check stock (${res.status})`);
    }

    return json;
  },
});
